import { Link } from "react-router-dom";
import { ArrowRight, Compass, Target, Eye, Award } from "lucide-react";

const VALUES = [
    {
        icon: Target,
        title: "Spec-First Sourcing",
        body: "Every cargo is matched to your boiler, kiln or furnace spec — not to whatever is cheapest at origin.",
    },
    {
        icon: Compass,
        title: "Long-Term Trade Lines",
        body: "Direct allocations with mines & terminals in 5 countries — no chain-of-middlemen surprises.",
    },
    {
        icon: Award,
        title: "Inspection Discipline",
        body: "Pre-shipment + discharge inspection by SGS, CCIC, Geo-Chem or Intertek on every parcel.",
    },
    {
        icon: Eye,
        title: "Operational Transparency",
        body: "Real-time vessel tracking, LOI to BOL trail shared with the buyer — open-book ops.",
    },
];

export default function About() {
    return (
        <div data-testid="page-about">
            {/* HERO */}
            <section className="relative pt-40 pb-20 bg-coal-900 border-b border-white/10 overflow-hidden">
                <div className="absolute right-0 top-0 w-1/2 h-full opacity-40">
                    <img
                        src="https://images.unsplash.com/photo-1759493464642-4e66d700fb77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxzdGVlbCUyMG1pbGwlMjBpbmR1c3RyaWFsJTIwcGxhbnR8ZW58MHx8fHwxNzc4Njg3OTczfDA&ixlib=rb-4.1.0&q=85"
                        alt="Industrial structure"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-coal-900 via-coal-900/80 to-transparent" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-6">
                        About — Carbex International
                    </div>
                    <h1 className="font-display font-bold text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92] text-white max-w-4xl">
                        Built on trade.
                        <br />
                        Run on trust.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-zinc-300 leading-relaxed">
                        Carbex International Private Limited is a specialised
                        coal-trading and logistics company — moving thermal
                        coal, coking coal and pet coke from origin terminals
                        across Indonesia, South Africa, Australia, the USA and
                        Russia, into the heart of Indian industry.
                    </p>
                </div>
            </section>

            {/* STORY */}
            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                            Our Story
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-[1]">
                            From a single shipment
                            <br />
                            to a steady drumbeat.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-zinc-300 leading-relaxed text-lg">
                        <p>
                            Carbex was founded with one stubborn idea — that
                            Indian buyers of imported coal deserve a trader who
                            speaks their language: tonnes, GCV, ash, sulphur,
                            and rake plans. Not one who hides behind brokerage
                            jargon.
                        </p>
                        <p>
                            We've grown from a single coastal trade route into
                            a multi-origin desk that handles bulk parcels of
                            Indonesian thermal, South African RB grades,
                            Australian premiums, US high-CV coal and Russian
                            Kuzbass — delivered CIF Krishnapatnam, Mundra,
                            Kandla, Tuticorin and inland by rake.
                        </p>
                        <p>
                            Today, Carbex supplies sponge iron mills, cement
                            plants, power utilities, brick kilns and FBC users
                            across India — and our customer retention is the
                            only KPI we care about.
                        </p>
                    </div>
                </div>
            </section>

            {/* VISION MISSION */}
            <section className="section bg-coal-800 border-y border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-px bg-white/10">
                    <div className="bg-coal-900 p-10 lg:p-14">
                        <Eye className="w-10 h-10 text-ember mb-6" />
                        <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                            Vision
                        </div>
                        <h3 className="font-display text-3xl font-semibold text-white mb-6 leading-tight">
                            To be India's most dependable coal-import desk —
                            where buyers never lose sleep over a cargo.
                        </h3>
                    </div>
                    <div className="bg-coal-900 p-10 lg:p-14">
                        <Target className="w-10 h-10 text-ember mb-6" />
                        <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                            Mission
                        </div>
                        <h3 className="font-display text-3xl font-semibold text-white mb-6 leading-tight">
                            Deliver the right grade, at the right port, at the
                            right CIF — every parcel, every quarter, every
                            year.
                        </h3>
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                        Operating Principles
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white max-w-3xl">
                        Trust isn't a tagline.
                        <br />
                        It's how we trade.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                        {VALUES.map((v) => (
                            <div
                                key={v.title}
                                className="bg-coal-800 p-8 hover:bg-coal-700 transition-colors"
                            >
                                <v.icon className="w-8 h-8 text-ember mb-6" />
                                <h4 className="font-display text-xl font-semibold text-white mb-3">
                                    {v.title}
                                </h4>
                                <p className="text-zinc-400 leading-relaxed text-sm">
                                    {v.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section bg-coal-800 border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                    <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-[1]">
                        Looking for a long-term
                        <br />
                        coal partner?
                    </h2>
                    <Link
                        to="/contact"
                        className="group bg-ember hover:bg-ember-hover text-white px-8 py-5 font-bold tracking-[0.18em] uppercase text-sm transition-colors flex items-center gap-3 self-start lg:self-auto"
                    >
                        Talk to the trade desk
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
